/** One-off payslips: paid outside the monthly runs, `routers/one_off_payslip.py` and the `/api/me/payslips/one-off` portal routes. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from './api'
import type { BankExport } from './payroll'

export type OneOffStatus = 'Draft' | 'Issued' | 'Void'
export type OneOffLineKind = 'leave_payout' | 'bonus' | 'correction' | 'other'

export interface OneOffLine {
  kind: OneOffLineKind
  label: string
  amount: string
}

interface PayoutTerm {
  kind: 'cl' | 'psl' | 'al' | 'al_prepaid' | 'al_deduct' | string
  days: string | null
  day_value: string | null
  amount: string | null
}

export interface OneOffWorking {
  lines: OneOffLine[]
  gross: string
  social_cap: string | null
  insured_before: string
  cap_left: string | null
  social_base: string
  soc_code: string
  soc_percent: string
  social_exempt: boolean
  social: string
  tax_base: string
  tax_percent: string
  tax_exempt: boolean
  tax: string
  net: string
  warnings: string[]
}

export interface OneOff extends OneOffWorking {
  id: string
  employee_id: string
  employee_name: string
  pay_date: string
  year: number
  month: number
  status: OneOffStatus
  reason: string
  void_reason: string
  supersedes_id: string | null
  resignation_payout_id: string | null
  payout_total: string | null
  payout_terms: PayoutTerm[]
  issued_at: string | null
  voided_at: string | null
  bank_exported_at: string | null
}

export interface OneOffDraft {
  employee_id: string
  pay_date: string
  lines: OneOffLine[]
  reason: string
}

export interface MailOutcome {
  queued: number
  already_sent: number
  skipped: { reason: string }[]
  suppressed: boolean
  to: string
  address: '' | 'work' | 'personal'
}

export interface MyOneOff {
  id: string
  pay_date: string
  year: number
  month: number
  status: OneOffStatus
  gross: string
  social: string
  tax: string
  net: string
  void_reason: string
  supersedes_id: string | null
}

const BASE = '/api/one-off-payslips'
const keys = {
  all: ['one-off-payslips'] as const,
  year: (year: number) => ['one-off-payslips', year] as const,
  mine: (start: string | null, end: string | null) => ['me', 'one-off-payslips', start, end] as const,
}

export function useOneOffs(year: number) {
  return useQuery({
    queryKey: keys.year(year),
    queryFn: async () => (await api.get<OneOff[]>(BASE, { params: { year } })).data,
  })
}

/** The working as Issue would price it now; `null` asks for nothing. */
export function useOneOffPreview(draft: (OneOffDraft & { id?: string }) | null) {
  return useQuery({
    queryKey: ['one-off-payslips', 'preview', draft],
    queryFn: async () => (await api.post<OneOffWorking>(`${BASE}/preview`, draft)).data,
    enabled: draft != null,
    placeholderData: (previous) => previous,
    retry: false,
  })
}

function useOneOffMutation<T>(run: (input: T) => Promise<OneOff>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.all }),
  })
}

export function useCreateOneOff() {
  return useOneOffMutation(async (draft: OneOffDraft) => (await api.post<OneOff>(BASE, draft)).data)
}

export function useEditOneOff() {
  return useOneOffMutation(
    async ({ id, ...draft }: OneOffDraft & { id: string }) =>
      (await api.put<OneOff>(`${BASE}/${id}`, {
        pay_date: draft.pay_date,
        lines: draft.lines,
        reason: draft.reason,
      })).data,
  )
}

export function useIssueOneOff() {
  return useOneOffMutation(async (id: string) => (await api.post<OneOff>(`${BASE}/${id}/issue`)).data)
}

export function useVoidOneOff() {
  return useOneOffMutation(
    async ({ id, reason, reissue }: { id: string; reason: string; reissue: boolean }) =>
      (await api.post<OneOff>(`${BASE}/${id}/void`, { reason, reissue })).data,
  )
}

export function useMailOneOff() {
  return useMutation({
    mutationFn: async (id: string) => (await api.post<MailOutcome>(`${BASE}/${id}/mail`)).data,
  })
}

/** Fetched on click, never cached: every call stamps the rows it sends and is logged. */
export async function oneOffBankExport(year: number, month: number, includeSent = false): Promise<BankExport> {
  return (await api.get<BankExport>(`${BASE}/bank-export`, { params: { year, month, include_sent: includeSent } })).data
}

export function oneOffPdfUrl(id: string, lang: string): string {
  return `${BASE}/${id}/pdf?lang=${lang}`
}

export function useMyOneOffs(period: { start: string; end: string } | null) {
  return useQuery({
    queryKey: keys.mine(period?.start ?? null, period?.end ?? null),
    queryFn: async () =>
      (await api.get<MyOneOff[]>('/api/me/payslips/one-off', { params: period ?? undefined })).data,
    enabled: period != null,
  })
}

export function useMyOneOff(id: string | null) {
  return useQuery({
    queryKey: ['me', 'one-off-payslip', id],
    queryFn: async () => (await api.get<OneOff>(`/api/me/payslips/one-off/${id}`)).data,
    enabled: id != null,
  })
}

export function myOneOffPdfUrl(id: string, lang: string): string {
  return `/api/me/payslips/one-off/${id}/pdf?lang=${lang}`
}
